import { useState } from "react";
import { Globe, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { localeCountry, regionLabel } from "../lib/regions";
import api from "../lib/api";

const DISMISS_KEY = "dial_region_prompt_dismissed";

export default function RegionPrompt() {
  const { user, patchUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  const suggested = localeCountry();
  const current = (user?.region || "US").toUpperCase();

  const shouldShow =
    user && user.onboarded && !user.region_set && !hidden &&
    suggested && suggested !== current;

  if (!shouldShow) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* no-op */ }
    setHidden(true);
  };

  const setRegion = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.put("/user/region", { region: code });
      patchUser(data);
    } catch {
      /* no-op */
    } finally {
      dismiss();
      setBusy(false);
    }
  };

  return (
    <div className="sticky top-0 z-[60] w-full border-b border-[var(--border-accent)] bg-[var(--bg-surface)]" data-testid="region-prompt-banner">
      <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between lg:px-14">
        <p className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
          <Globe size={16} className="flex-none text-[var(--accent)]" />
          Watching from {regionLabel(suggested)}? Set your region for accurate listings.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRegion(suggested)}
            disabled={busy}
            className="rounded-[var(--card-radius)] bg-[var(--accent)] px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)] disabled:opacity-60"
            data-testid="region-prompt-set"
          >
            Set to {suggested}
          </button>
          <button
            onClick={() => setRegion(current)}
            disabled={busy}
            className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-4 py-1.5 text-xs font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-60"
            data-testid="region-prompt-keep"
          >
            Keep {current}
          </button>
          <button onClick={dismiss} aria-label="Dismiss" className="text-[var(--text-muted)] hover:text-[var(--text-primary)]" data-testid="region-prompt-dismiss">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
